import React, { useCallback, useEffect, useState } from 'react';
import { CalendarDays, Clock, ListChecks, MapPin, RefreshCw, Save, Users } from 'lucide-react';
import { attendanceAPI, locationAPI } from '../../services/api';
import { useToast } from '../UIProvider';
import Select from '../Select';
import { TIME_OPTIONS } from '../../utils/doctorTimings';
import { WEEK_DAYS, dayLabel } from './attendanceStatus';
import HolidaysSettings from './HolidaysSettings';
import LeaveTypesSettings from './LeaveTypesSettings';

// The rules attendance is judged by: the working week and hours, how late is late, how many hours
// make a full or a half day, when a forgotten punch-out is closed, and how long selfies are kept.
// The hospital's position (what "at the hospital" means) is set on the Locations page; this page
// only says whether it is.

const NUMBER_FIELDS = [
  { key: 'lateGraceMinutes', label: 'Late after (minutes past the start time)', min: 0, max: 180, hint: 'Punching in later than this counts as late.' },
  { key: 'fullDayHours', label: 'Hours for a full day', min: 1, max: 16, step: 0.5, hint: 'At or above this the day is Present. Somebody whose own hours are shorter than this is judged against their own shift instead, so a short roster worked in full still counts as a full day.' },
  { key: 'halfDayHours', label: 'Hours for a half day', min: 0.5, max: 16, step: 0.5, hint: 'At or above this, but under a full day, is a Half day. Under it the day is Absent.' },
  { key: 'autoCloseHours', label: 'Close a forgotten punch-out after (hours past the end time)', min: 0, max: 24, hint: 'The system then closes the shift at the end time and lists it under Corrections.' },
  { key: 'photoRetentionDays', label: 'Keep selfies for (days)', min: 7, max: 3650, hint: 'Older selfies are deleted automatically once a day. The punch itself is kept.' },
];

const AttendanceSettings = ({ hasPermission }) => {
  const notify = useToast();
  const can = (action) => (typeof hasPermission === 'function' ? hasPermission('ATTENDANCE', action) : true);
  const canEdit = can('EDIT');
  const canHolidays = can('MANAGE_HOLIDAYS');
  // The holiday calendar and everybody's hours have permissions of their own, so they are tabs of
  // their own: somebody may be trusted to set the working rules without also deciding who is
  // expected in on which day. Before this, those two actions only greyed the panel out, so anyone
  // who could open the page could still read both.
  //
  // Rules and Leave types are NOT gated on EDIT. The page is read-only without it, which is the
  // state somebody reaches by typing the address rather than following the sidebar link, and
  // blanking the page would lose that reading rather than protect anything.
  const tabs = [
    { key: 'rules', label: 'Rules', icon: Clock, allowed: true },
    { key: 'holidays', label: 'Holidays', icon: CalendarDays, allowed: canHolidays },
    { key: 'leaveTypes', label: 'Leave types', icon: ListChecks, allowed: true },
  ].filter((t) => t.allowed);
  const [tab, setTab] = useState('rules');
  // Whoever lands on a tab they may not open gets the first one they may.
  const current = tabs.some((t) => t.key === tab) ? tab : tabs[0]?.key;

  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [loadError, setLoadError] = useState('');
  const [hospital, setHospital] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const [settings, locations] = await Promise.all([attendanceAPI.getSettings(), locationAPI.getAll().catch(() => [])]);
      setForm({
        ...settings,
        lateGraceMinutes: String(settings.lateGraceMinutes ?? ''),
        fullDayHours: String(settings.fullDayHours ?? ''),
        halfDayHours: String(settings.halfDayHours ?? ''),
        autoCloseHours: String(settings.autoCloseHours ?? ''),
        photoRetentionDays: String(settings.photoRetentionDays ?? ''),
      });
      setHospital((Array.isArray(locations) ? locations : []).find((l) => l.isDefault) || null);
    } catch (e) {
      setLoadError(e?.message || 'The attendance settings could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const toggleDay = (day) => setForm((f) => ({
    ...f,
    workDays: f.workDays.includes(day) ? f.workDays.filter((d) => d !== day) : [...f.workDays, day],
  }));

  const problem = () => {
    if (!form.workDays.length) return 'Pick at least one working day.';
    if (form.endTime <= form.startTime) return 'The end time must be after the start time.';
    for (const f of NUMBER_FIELDS) {
      const n = Number(form[f.key]);
      if (form[f.key] === '' || Number.isNaN(n)) return `Enter ${f.label.toLowerCase()}.`;
      if (n < f.min || n > f.max) return `${f.label} must be between ${f.min} and ${f.max}.`;
    }
    if (Number(form.halfDayHours) >= Number(form.fullDayHours)) return 'A half day must be fewer hours than a full day.';
    return null;
  };

  const save = async () => {
    const p = problem();
    if (p) { setError(p); return; }
    setError('');
    setSaving(true);
    try {
      const saved = await attendanceAPI.updateSettings({
        workDays: WEEK_DAYS.filter((d) => form.workDays.includes(d)),
        startTime: form.startTime,
        endTime: form.endTime,
        lateGraceMinutes: Number(form.lateGraceMinutes),
        fullDayHours: Number(form.fullDayHours),
        halfDayHours: Number(form.halfDayHours),
        autoCloseHours: Number(form.autoCloseHours),
        photoRetentionDays: Number(form.photoRetentionDays),
      });
      setForm((f) => ({ ...f, ...saved, lateGraceMinutes: String(saved.lateGraceMinutes), fullDayHours: String(saved.fullDayHours), halfDayHours: String(saved.halfDayHours), autoCloseHours: String(saved.autoCloseHours), photoRetentionDays: String(saved.photoRetentionDays) }));
      notify('success', 'Attendance settings saved');
    } catch (e) {
      setError(e?.message || 'The settings could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-navy-50 via-white to-teal-50 p-6">
        <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-xl p-12 text-center">
          {loadError ? (
            <>
              <p className="text-red-700">{loadError}</p>
              <button type="button" onClick={load} className="mt-3 text-sm font-medium text-navy-700 underline">Try again</button>
            </>
          ) : (
            <>
              <RefreshCw className="h-8 w-8 text-emerald-500 animate-spin mx-auto mb-3" />
              <p className="text-gray-500">Loading attendance settings…</p>
            </>
          )}
        </div>
      </div>
    );
  }

  const positioned = hospital && hospital.latitude != null && hospital.longitude != null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-50 via-white to-teal-50 p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="bg-white rounded-2xl shadow-xl p-6 flex items-center space-x-4">
          <div className="w-14 h-14 bg-gradient-to-br from-navy-700 to-navy-900 rounded-2xl flex items-center justify-center">
            <Clock className="h-7 w-7 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Attendance</h1>
            <p className="text-gray-600">The working week, holidays, leave types and each person's hours</p>
          </div>
        </div>

        <div className="flex gap-2 flex-wrap" role="group" aria-label="Attendance settings">
          {tabs.map((t) => {
            const Icon = t.icon;
            const active = current === t.key;
            return (
              <button
                key={t.key}
                type="button"
                aria-pressed={active}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${active ? 'bg-navy-800 text-white' : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'}`}
              >
                <Icon className="h-4 w-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {current === 'holidays' && <HolidaysSettings canEdit={canHolidays} />}
        {current === 'leaveTypes' && <LeaveTypesSettings canEdit={canEdit} />}

        {current === 'rules' && (
          <>

        <div className={`rounded-2xl shadow-xl p-5 flex items-start gap-3 ${positioned ? 'bg-white' : 'bg-amber-50 border border-amber-200'}`}>
          <MapPin className={`w-5 h-5 flex-shrink-0 mt-0.5 ${positioned ? 'text-emerald-600' : 'text-amber-600'}`} aria-hidden="true" />
          <div className="text-sm">
            {positioned ? (
              <p className="text-gray-700">
                Punches are checked against <span className="font-medium text-gray-900">{hospital.name}</span>, within {hospital.radiusMeters || 150} m
                ({Number(hospital.latitude).toFixed(5)}, {Number(hospital.longitude).toFixed(5)}). Change it under Settings, Timezone Settings.
              </p>
            ) : (
              <p className="text-amber-900">
                The hospital's position is not set, so punches are recorded without checking where they were made.
                Set it under Settings, Timezone Settings, by editing the default location while standing at the hospital.
              </p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-6 space-y-5">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Working week</h2>
            <p className="text-sm text-gray-600">The hospital's hours. Anyone who works different hours gets their own under "Hours per person"; days off are never counted as absent.</p>
          </div>
          <div className="flex flex-wrap gap-2" role="group" aria-label="Working days">
            {WEEK_DAYS.map((d) => {
              const on = form.workDays.includes(d);
              return (
                <button
                  key={d}
                  type="button"
                  aria-pressed={on}
                  disabled={!canEdit || saving}
                  onClick={() => toggleDay(d)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium border ${on ? 'bg-navy-800 text-white border-navy-800' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'} disabled:opacity-60`}
                >
                  {dayLabel(d)}
                </button>
              );
            })}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Select label="Start time" value={form.startTime} onChange={(v) => set('startTime', v)} options={TIME_OPTIONS} disabled={!canEdit || saving} />
            <Select label="End time" value={form.endTime} onChange={(v) => set('endTime', v)} options={TIME_OPTIONS} disabled={!canEdit || saving} />
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-6 space-y-5">
          <h2 className="text-lg font-semibold text-gray-900">Rules</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {NUMBER_FIELDS.map((f) => (
              <div key={f.key}>
                <label htmlFor={`att-${f.key}`} className="block text-sm font-semibold mb-1">{f.label}</label>
                <input
                  id={`att-${f.key}`}
                  type="number"
                  min={f.min}
                  max={f.max}
                  step={f.step || 1}
                  value={form[f.key]}
                  onChange={(e) => set(f.key, e.target.value)}
                  disabled={!canEdit || saving}
                  className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 disabled:bg-gray-50"
                />
                <p className="mt-1 text-xs text-gray-500">{f.hint}</p>
              </div>
            ))}
          </div>
          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
          {canEdit ? (
            <div className="flex justify-end">
              <button type="button" onClick={save} disabled={saving} className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium disabled:opacity-50">
                <Save className="h-4 w-4" aria-hidden="true" /> {saving ? 'Saving…' : 'Save settings'}
              </button>
            </div>
          ) : (
            <p className="text-xs text-gray-500 italic">You don't have permission to change attendance settings.</p>
          )}
        </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AttendanceSettings;
